import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LogOut,
  Search,
  X,
  ShoppingCart,
  Wrench,
  BarChart3,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";

const API = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 50;
const hdr = () => ({
  Authorization: `Bearer ${localStorage.getItem("e93_inventory_seller_token")}`,
});
const money = (v) =>
  `${Math.round((v || 0) / 100).toLocaleString("sq-AL")} Lek`;
const img = (url) =>
  url ? (url.startsWith("http") ? url : `${API}${url}`) : "";
const isCigarette = (product) =>
  (product.name || product.product_name || "").toLowerCase().includes("cigare");
const stockText = (product, item) => {
  const tracked = item.track_by_pack ?? product.track_by_pack;
  if (!tracked) {
    const stock = Number(item.stock || 0);
    return isCigarette(product)
      ? `${stock} ${stock === 1 ? "paketë" : "paketa"}`
      : `${stock} copë`;
  }
  const perPack = Number(item.pieces_per_pack || product.pieces_per_pack || 20);
  const stock = Number(item.stock || 0);
  const packs = Math.floor(stock / perPack);
  const pieces = stock % perPack;
  if (!pieces) return `${packs} ${packs === 1 ? "paketë" : "paketa"}`;
  if (!packs) return `${pieces} cigare`;
  return `${packs} ${packs === 1 ? "paketë" : "paketa"} dhe ${pieces} cigare`;
};
const dayKey = (value) =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Tirane",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(value));
const isPhone = (s) => ["Telefona", "Aksesorë telefonash"].includes(s.category);
const isTobacco = (s) => s.category === "Produkte Tabakino";
const saleQtyText = (s) =>
  s.sale_type === "pack"
    ? `${s.packages} paketë (${s.quantity} copë)`
    : s.sale_type === "loose"
      ? `${s.quantity} copë të hapura`
      : isCigarette(s)
        ? `${s.quantity} ${s.quantity === 1 ? "paketë" : "paketa"}`
        : `${s.quantity} copë`;
const emptyService = () => ({
  service_type: "Ndërrim ekrani",
  device_model: "",
  description: "",
  amount: "",
  product_cost: "",
  transport_cost: "",
});
const norm = (v) =>
  (v || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
const distance = (a, b) => {
  const d = Array.from({ length: a.length + 1 }, (_, i) => [i]);
  for (let j = 1; j <= b.length; j++) d[0][j] = j;
  for (let i = 1; i <= a.length; i++)
    for (let j = 1; j <= b.length; j++)
      d[i][j] = Math.min(
        d[i - 1][j] + 1,
        d[i][j - 1] + 1,
        d[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
  return d[a.length][b.length];
};
const wordMatch = (token, text) =>
  text.includes(token) ||
  (token.length >= 5 &&
    text
      .split(/\s+/)
      .some(
        (word) =>
          Math.abs(word.length - token.length) <= 2 &&
          distance(token, word) <= 2,
      ));
const matchingVariants = (p, query) => {
  const tokens = norm(query).split(/\s+/).filter(Boolean);
  if (!tokens.length) return p.variants || [];
  return (p.variants || []).filter((v) => {
    const tracked = v.track_by_pack ?? p.track_by_pack;
    const text = norm(
      [
        p.name,
        p.sku,
        p.brand,
        p.category,
        v.model,
        tracked ? "pakete paketë e hapur cope copë" : "",
      ].join(" "),
    );
    return tokens.every((t) => wordMatch(t, text));
  });
};

export default function Seller() {
  const nav = useNavigate();
  const [catalog, setCatalog] = useState([]),
    [sales, setSales] = useState([]),
    [services, setServices] = useState([]),
    [dailyServiceRows, setDailyServiceRows] = useState([]),
    [periodStart, setPeriodStart] = useState(""),
    [periodActive, setPeriodActive] = useState(false);
  const [search, setSearch] = useState(""),
    [productFilter, setProductFilter] = useState("all"),
    [productPage, setProductPage] = useState(1),
    [expanded, setExpanded] = useState({});
  const [qty, setQty] = useState({}),
    [amounts, setAmounts] = useState({}),
    [service, setService] = useState(emptyService());
  const logout = useCallback(() => {
    localStorage.removeItem("e93_inventory_seller_token");
    nav("/inventory/seller/login");
  }, [nav]);
  const load = useCallback(async () => {
    try {
      const [c, s, sv] = await Promise.all([
        axios.get(`${API}/api/inventory/catalog`, { headers: hdr() }),
        axios.get(`${API}/api/inventory/sales/mine`, { headers: hdr() }),
        axios.get(`${API}/api/inventory/services/mine`, { headers: hdr() }),
      ]);
      setCatalog(c.data);
      setSales(s.data);
      setServices(sv.data.services || []);
      setDailyServiceRows(sv.data.daily_services || []);
      setPeriodStart(sv.data.period_started_at || "");
      setPeriodActive(sv.data.period_active !== false);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) logout();
    }
  }, [logout]);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    const refresh = () => load();
    const onVisibility = () => {
      if (document.visibilityState === "visible") load();
    };
    const timer = window.setInterval(refresh, 15000);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [load]);
  useEffect(() => {
    const token = localStorage.getItem("e93_inventory_seller_token");
    if (!token) return logout();
    try {
      const encoded = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
      const payload = JSON.parse(
        decodeURIComponent(
          atob(encoded.padEnd(Math.ceil(encoded.length / 4) * 4, "="))
            .split("")
            .map((c) => `%${("00" + c.charCodeAt(0).toString(16)).slice(-2)}`)
            .join(""),
        ),
      );
      const remaining = payload.exp * 1000 - Date.now();
      if (remaining <= 0) return logout();
      const timer = window.setTimeout(logout, remaining);
      return () => window.clearTimeout(timer);
    } catch {
      logout();
    }
  }, [logout]);
  const shown = useMemo(
    () =>
      catalog
        .filter(
          (p) =>
            productFilter === "all" ||
            (productFilter === "phone" &&
              ["Telefona", "Aksesorë telefonash"].includes(p.category)) ||
            (productFilter === "tobacco" &&
              p.category === "Produkte Tabakino") ||
            (productFilter === "other" &&
              ![
                "Telefona",
                "Aksesorë telefonash",
                "Produkte Tabakino",
              ].includes(p.category)),
        )
        .filter((p) => matchingVariants(p, search).length > 0),
    [catalog, search, productFilter],
  );
  const pages = Math.max(1, Math.ceil(shown.length / PAGE_SIZE)),
    page = Math.min(productPage, pages),
    pagedProducts = shown.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const today = dayKey(new Date()),
    dailyServices = dailyServiceRows.filter(
      (s) => dayKey(s.created_at) === today,
    ),
    amountCents = Math.round(Number(service.amount || 0) * 100),
    productCostCents = Math.round(Number(service.product_cost || 0) * 100),
    transportCostCents = Math.round(Number(service.transport_cost || 0) * 100),
    netProfitCents = amountCents - productCostCents - transportCostCents,
    sellerShare = Math.floor(Math.max(0, netProfitCents) / 2);
  const phoneSales = sales.filter(isPhone),
    tobaccoSales = sales.filter(isTobacco),
    otherSales = sales.filter((s) => !isPhone(s) && !isTobacco(s));
  const sell = async (p, v, type = "normal") => {
    const quantity = Number(qty[v.id] || 1),
      amount = Math.round(Number(amounts[v.id] || 0) * 100);
    try {
      const { data } = await axios.post(
        `${API}/api/inventory/sales`,
        {
          product_id: p.id,
          variant_id: v.id,
          quantity,
          sale_type: type,
          amount: type === "loose" ? amount : 0,
          note: "",
        },
        { headers: hdr() },
      );
      toast.success(
        type === "pack"
          ? `${quantity} paketë u regjistruan`
          : type === "loose"
            ? `${data.quantity} copë të hapura u regjistruan`
            : isCigarette(p)
              ? `${quantity} paketë u regjistruan`
              : `${quantity} copë u regjistruan`,
      );
      setQty((x) => ({ ...x, [v.id]: 1 }));
      setAmounts((x) => ({ ...x, [v.id]: "" }));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Shitja nuk u regjistrua");
    }
  };
  const saveService = async (e) => {
    e.preventDefault();
    if (amountCents <= 0) return toast.error("Vendosni vlerën e servisit");
    if (netProfitCents < 0)
      return toast.error("Kostot nuk mund të kalojnë vlerën e servisit");
    try {
      await axios.post(
        `${API}/api/inventory/services`,
        {
          ...service,
          amount: amountCents,
          product_cost: productCostCents,
          transport_cost: transportCostCents,
        },
        { headers: hdr() },
      );
      toast.success(`Servisi u regjistrua. Pjesa jote: ${money(sellerShare)}`);
      setService(emptyService());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Servisi nuk u regjistrua");
    }
  };
  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-slate-950 text-white p-3 sm:p-4 flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2 sm:gap-3">
          <img
            src="/e93-logo.jpg"
            alt="E93"
            className="w-11 h-11 rounded-full object-cover"
          />
          <div className="min-w-0">
            <b className="block text-sm sm:text-base">Paneli i Shitësit E93</b>
            <p className="text-xs text-slate-400">Shitje dhe servise</p>
          </div>
        </div>
        <Button
          variant="outline"
          className="bg-transparent text-white"
          onClick={logout}
        >
          <LogOut className="h-4 w-4 sm:mr-1" />
          <span className="hidden sm:inline">Dil</span>
        </Button>
      </header>
      <main className="max-w-5xl mx-auto p-2.5 sm:p-4">
        {!periodActive && (
          <Card className="my-5 border-amber-300 bg-amber-50 shadow-sm">
            <CardContent className="p-6 text-center">
              <h2 className="text-xl font-bold text-amber-900">
                Periudha e muajit nuk është hapur
              </h2>
              <p className="mt-2 text-amber-800">
                Ju lutemi kërkoni të hapet periudha.
              </p>
            </CardContent>
          </Card>
        )}
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4 md:gap-3 my-3 sm:my-4">
          <CompactCard
            label="Telefonia sot"
            value={money(phoneSales.reduce((a, s) => a + s.total, 0))}
            detail={`${phoneSales.reduce((a, s) => a + s.quantity, 0)} copë`}
          />
          <CompactCard
            label="Tabakino sot"
            value={money(tobaccoSales.reduce((a, s) => a + s.total, 0))}
            detail={`${tobaccoSales.reduce((a, s) => a + s.quantity, 0)} copë`}
          />
          <CompactCard
            label="Servise sot"
            value={money(dailyServices.reduce((a, s) => a + (s.net_profit ?? s.amount), 0))}
            detail={`${dailyServices.length} servise • fitimi pas kostove`}
          />
          <CompactCard
            dark
            label="Xhiro totale sot"
            value={money(
              sales.reduce((a, s) => a + s.total, 0) +
                dailyServices.reduce((a, s) => a + (s.net_profit ?? s.amount), 0),
            )}
            detail="Shitjet + serviset e plota"
          />
        </div>
        <Tabs
          defaultValue="products"
          className={!periodActive ? "pointer-events-none opacity-50" : ""}
        >
          <TabsList className="mb-2 flex h-auto w-full justify-start overflow-x-auto p-1">
            <TabsTrigger value="products" className="shrink-0 text-xs sm:text-sm">
              <ShoppingCart className="h-4 w-4 mr-1" />
              Shitje produktesh
            </TabsTrigger>
            <TabsTrigger value="daily" className="shrink-0 text-xs sm:text-sm">
              <BarChart3 className="h-4 w-4 mr-1" />
              Shitjet e ditës
            </TabsTrigger>
            <TabsTrigger value="services" className="shrink-0 text-xs sm:text-sm">
              <Wrench className="h-4 w-4 mr-1" />
              Servise
            </TabsTrigger>
          </TabsList>
          <TabsContent value="products">
            <div className="sticky top-0 z-10 bg-slate-100 py-3">
              <div className="flex flex-wrap gap-2 mb-3">
                {[
                  ["all", "Të gjitha"],
                  ["phone", "Telefonia"],
                  ["tobacco", "Tabakino"],
                  ["other", "Të tjera"],
                ].map(([key, label]) => (
                  <Button
                    size="sm"
                    key={key}
                    variant={productFilter === key ? "default" : "outline"}
                    onClick={() => {
                      setProductFilter(key);
                      setProductPage(1);
                    }}
                  >
                    {label}
                  </Button>
                ))}
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4" />
                <Input
                  className="pl-9 pr-10 bg-white"
                  placeholder="Kërko produkt ose model..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setProductPage(1);
                  }}
                />
                {search && (
                  <button
                    type="button"
                    aria-label="Pastro kërkimin"
                    className="absolute right-3 top-2.5 text-gray-500 hover:text-black"
                    onClick={() => {
                      setSearch("");
                      setProductPage(1);
                    }}
                  >
                    <X className="h-5 w-5" />
                  </button>
                )}
              </div>
            </div>
            <div className="flex justify-between items-center mb-3">
              <b>Produktet për shitje</b>
              <span className="text-sm text-gray-500">
                {shown.length ? (page - 1) * PAGE_SIZE + 1 : 0}–
                {Math.min(page * PAGE_SIZE, shown.length)} nga {shown.length}
              </span>
            </div>
            <div className="space-y-3">
              {pagedProducts.map((p) => {
                const visible = matchingVariants(p, search),
                  isOpen = Boolean(search.trim()) || expanded[p.id];
                return (
                  <Card key={p.id}>
                    <CardContent className="p-0">
                      <div className="p-3 sm:p-4 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex min-w-0 items-center gap-3">
                          {p.image_url && (
                            <img
                              className="h-14 w-14 shrink-0 rounded-md object-cover"
                              src={img(p.image_url)}
                              alt={p.name}
                            />
                          )}
                          <div className="min-w-0">
                            <b>{p.name}</b>
                            <p className="text-sm text-gray-500">
                              {p.brand} • {p.category} • {p.sku}
                            </p>
                            <small>
                              {visible.length} {search ? "rezultate" : "modele"}
                            </small>
                          </div>
                        </div>
                        <Button
                          className="w-full sm:w-auto"
                          variant="outline"
                          onClick={() =>
                            setExpanded((x) => ({ ...x, [p.id]: !isOpen }))
                          }
                        >
                          {isOpen ? (
                            <ChevronUp className="h-4 w-4 mr-1" />
                          ) : (
                            <ChevronDown className="h-4 w-4 mr-1" />
                          )}
                          {isOpen
                            ? "Mbyll modelet"
                            : `Shfaq modelet (${visible.length})`}
                        </Button>
                      </div>
                      {isOpen && (
                        <div className="border-t p-4 space-y-4">
                          {visible.map((v) => (
                            <VariantSale
                              key={v.id}
                              product={p}
                              variant={v}
                              qty={qty}
                              setQty={setQty}
                              amounts={amounts}
                              setAmounts={setAmounts}
                              sell={sell}
                            />
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            <Pagination page={page} pages={pages} onChange={setProductPage} />
          </TabsContent>
          <TabsContent value="daily">
            <DailySection title="Shitjet e Telefonisë sot" rows={phoneSales} />
            <DailySection
              title="Shitjet e Produkteve Tabakino sot"
              rows={tobaccoSales}
            />
            {otherSales.length > 0 && (
              <DailySection title="Shitje të tjera sot" rows={otherSales} />
            )}
          </TabsContent>
          <TabsContent value="services">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 my-4">
              <StatCard label="Servise sot" value={dailyServices.length} />
              <StatCard
                label="Pjesa ime sot"
                value={money(
                  dailyServices.reduce((a, s) => a + s.seller_share, 0),
                )}
              />
              <StatCard
                label="Servise për muajin"
                value={periodActive ? services.length : 0}
              />
              <StatCard
                label="Fitimi im për muajin"
                value={money(
                  periodActive
                    ? services.reduce((a, s) => a + s.seller_share, 0)
                    : 0,
                )}
                detail={
                  periodActive && periodStart
                    ? `Nga ${new Date(periodStart).toLocaleDateString("sq-AL")}`
                    : "Periudha është e mbyllur"
                }
              />
            </div>
            <Card className="my-4">
              <CardContent className="p-3 sm:p-5">
                <h2 className="text-xl font-bold mb-4">Regjistro një servis</h2>
                <form onSubmit={saveService} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Lloji i servisit *</Label>
                      <select
                        className="w-full border rounded-md h-10 px-3"
                        value={service.service_type}
                        onChange={(e) =>
                          setService({
                            ...service,
                            service_type: e.target.value,
                          })
                        }
                      >
                        <option>Ndërrim ekrani</option>
                        <option>Ndërrim qarku</option>
                        <option>Ndërrim baterie</option>
                        <option>Riparim porte karikimi</option>
                        <option>Software / formatim</option>
                        <option>Tjetër</option>
                      </select>
                    </div>
                    <div>
                      <Label>Telefoni / modeli</Label>
                      <Input
                        value={service.device_model}
                        onChange={(e) =>
                          setService({
                            ...service,
                            device_model: e.target.value,
                          })
                        }
                        placeholder="p.sh. iPhone 13"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Vlera e servisit (Lek) *</Label>
                    <Input
                      required
                      min="1"
                      type="number"
                      value={service.amount}
                      onChange={(e) =>
                        setService({ ...service, amount: e.target.value })
                      }
                      placeholder="p.sh. 1000"
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Kostoja e produktit / pjesës (Lek)</Label>
                      <Input
                        min="0"
                        type="number"
                        value={service.product_cost}
                        onChange={(e) =>
                          setService({ ...service, product_cost: e.target.value })
                        }
                        placeholder="0 nëse nuk ka pjesë"
                      />
                    </div>
                    <div>
                      <Label>Kostoja e transportit (Lek)</Label>
                      <Input
                        min="0"
                        type="number"
                        value={service.transport_cost}
                        onChange={(e) =>
                          setService({ ...service, transport_cost: e.target.value })
                        }
                        placeholder="0 nëse nuk ka transport"
                      />
                    </div>
                  </div>
                  <div className="rounded-md bg-emerald-50 border border-emerald-200 p-4">
                    <p>
                      Vlera e servisit: <b>{money(amountCents)}</b>
                    </p>
                    <p>Kostoja e pjesës: <b>{money(productCostCents)}</b></p>
                    <p>Kostoja e transportit: <b>{money(transportCostCents)}</b></p>
                    <p className="border-t mt-2 pt-2">
                      Fitimi pas kostove: <b>{money(Math.max(0, netProfitCents))}</b>
                    </p>
                    <p>
                      Fitimi yt (50%):{" "}
                      <b className="text-emerald-700">{money(sellerShare)}</b>
                    </p>
                    <p>
                      Pjesa e pronarit (50%):{" "}
                      <b>{money(Math.max(0, netProfitCents) - sellerShare)}</b>
                    </p>
                  </div>
                  <div>
                    <Label>Shënime</Label>
                    <Textarea
                      value={service.description}
                      onChange={(e) =>
                        setService({ ...service, description: e.target.value })
                      }
                    />
                  </div>
                  <Button>
                    <Wrench className="h-4 w-4 mr-1" />
                    Regjistro servisin
                  </Button>
                </form>
              </CardContent>
            </Card>
            <DailySection
              title="Serviset e mia sot"
              rows={dailyServices}
              service
            />
            <DailySection title="Serviset e muajit" rows={services} service />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function CompactCard({ label, value, detail, dark }) {
  return (
    <Card className={dark ? "bg-slate-950 text-white" : ""}>
      <CardContent className="p-3 md:p-4">
        <p
          className={`text-xs md:text-sm ${dark ? "text-slate-300" : "text-gray-500"}`}
        >
          {label}
        </p>
        <b className="text-lg md:text-2xl block truncate">{value}</b>
        <small className={dark ? "text-slate-300" : ""}>{detail}</small>
      </CardContent>
    </Card>
  );
}
function StatCard({ label, value, detail }) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-sm text-gray-500">{label}</p>
        <b className="text-xl md:text-2xl text-emerald-700">{value}</b>
        {detail && <small className="block text-gray-500 mt-1">{detail}</small>}
      </CardContent>
    </Card>
  );
}
function VariantSale({
  product: p,
  variant: v,
  qty,
  setQty,
  amounts,
  setAmounts,
  sell,
}) {
  const empty = (v.stock || 0) <= 0,
    low = !empty && (v.stock || 0) <= (v.low_stock_threshold || 0),
    tracked = v.track_by_pack ?? p.track_by_pack,
    piecePrice = v.piece_price || p.piece_price;
  return (
    <div className="rounded-md border p-3">
      <div className="mb-3 flex flex-wrap justify-between gap-2">
        <div>
          <b>{v.model}</b>
          <p>
            {tracked
              ? `Paketa: ${money(v.selling_price)} • E hapur: ${money(piecePrice)} / copë`
              : money(v.selling_price)}
          </p>
        </div>
        <span
          className={`h-fit rounded-full px-3 py-1 text-xs font-semibold ${empty ? "bg-red-100 text-red-700" : low ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}
        >
          {empty ? "Nuk ka gjendje" : low ? "Stok i ulët" : "Në gjendje"}
        </span>
      </div>
      {isCigarette(p) && (
        <p className="mb-3 text-sm font-semibold text-slate-600">
          Gjendja: {stockText(p, v)}
        </p>
      )}
      {tracked ? (
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label>Shit paketë</Label>
            <div className="flex flex-col gap-2 mt-2 sm:flex-row">
              <Input
                disabled={empty}
                type="number"
                min="1"
                value={qty[v.id] || 1}
                onChange={(e) =>
                  setQty((x) => ({ ...x, [v.id]: e.target.value }))
                }
              />
              <Button className="w-full sm:w-auto" disabled={empty} onClick={() => sell(p, v, "pack")}>
                U shit paketë
              </Button>
            </div>
          </div>
          <div>
            <Label>Shit të hapura sipas vlerës</Label>
            <div className="flex flex-col gap-2 mt-2 sm:flex-row">
              <Input
                disabled={empty}
                type="number"
                min="1"
                placeholder="p.sh. 100 Lek"
                value={amounts[v.id] || ""}
                onChange={(e) =>
                  setAmounts((x) => ({ ...x, [v.id]: e.target.value }))
                }
              />
              <Button className="w-full sm:w-auto" disabled={empty} onClick={() => sell(p, v, "loose")}>
                U shit e hapur
              </Button>
            </div>
            <p className="text-sm text-emerald-700 mt-2">
              {Number(amounts[v.id] || 0) > 0 && piecePrice > 0
                ? `${Number(amounts[v.id])} Lek = ${Math.floor((Number(amounts[v.id]) * 100) / piecePrice)} copë`
                : "Vendos vlerën e shitjes"}
            </p>
          </div>
        </div>
      ) : (
        <div className="flex gap-2 justify-end">
          <Input
            disabled={empty}
            className="w-20 shrink-0"
            type="number"
            min="1"
            value={qty[v.id] || 1}
            onChange={(e) => setQty((x) => ({ ...x, [v.id]: e.target.value }))}
          />
          <Button disabled={empty} onClick={() => sell(p, v)}>
            <ShoppingCart className="h-4 w-4 mr-1" />U shit
          </Button>
        </div>
      )}
    </div>
  );
}
function DailySection({ title, rows, service = false }) {
  return (
    <section className="mt-5">
      <h2 className="text-lg font-bold mb-3">{title}</h2>
      <div className="hidden bg-white border overflow-x-auto sm:block">
        <table className="w-full min-w-[620px] text-sm">
          <thead>
            <tr className="text-left bg-slate-50">
              <th className="p-3">Ora</th>
              <th>{service ? "Servisi" : "Produkti"}</th>
              <th>{service ? "Fitimi pas kostove" : "Sasia"}</th>
              <th>{service ? "Pjesa ime" : "Totali"}</th>
            </tr>
          </thead>
          <tbody>
            {rows.length ? (
              rows.map((s) => (
                <tr className="border-t" key={s.id}>
                  <td className="p-3">
                    {new Date(s.created_at).toLocaleTimeString("sq-AL")}
                  </td>
                  <td>
                    {service
                      ? `${s.service_type} — ${s.device_model || "Pa model"}`
                      : `${s.product_name} — ${s.model}`}
                  </td>
                  <td>{service ? money(s.net_profit ?? s.amount) : saleQtyText(s)}</td>
                  <td className="font-semibold">
                    {money(service ? s.seller_share : s.total)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="p-5 text-center text-gray-500">
                  Nuk ka regjistrime.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="space-y-2 sm:hidden">
        {rows.length ? (
          rows.map((s) => (
            <Card key={s.id}>
              <CardContent className="p-3 text-sm">
                <div className="flex items-start justify-between gap-3">
                  <b className="break-words">
                    {service
                      ? `${s.service_type} — ${s.device_model || "Pa model"}`
                      : `${s.product_name} — ${s.model}`}
                  </b>
                  <span className="shrink-0 text-xs text-gray-500">
                    {new Date(s.created_at).toLocaleTimeString("sq-AL", { hour: "2-digit", minute: "2-digit" })}
                  </span>
                </div>
                <div className="mt-2 flex items-end justify-between gap-3 border-t pt-2">
                  <span className="text-gray-600">
                    {service ? `Fitimi: ${money(s.net_profit ?? s.amount)}` : saleQtyText(s)}
                  </span>
                  <b className="text-emerald-700">
                    {money(service ? s.seller_share : s.total)}
                  </b>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <div className="rounded-md border bg-white p-5 text-center text-sm text-gray-500">
            Nuk ka regjistrime.
          </div>
        )}
      </div>
    </section>
  );
}
function Pagination({ page, pages, onChange }) {
  if (pages <= 1) return null;
  return (
    <div className="flex justify-center items-center gap-2 sm:gap-3 mt-5">
      <Button
        variant="outline"
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
      >
        <span className="hidden sm:inline">E mëparshme</span>
        <span className="sm:hidden">Para</span>
      </Button>
      <span className="text-sm font-semibold">
        Faqja {page} nga {pages}
      </span>
      <Button
        variant="outline"
        disabled={page >= pages}
        onClick={() => onChange(page + 1)}
      >
        <span className="hidden sm:inline">Tjetra</span>
        <span className="sm:hidden">Pas</span>
      </Button>
    </div>
  );
}
